/**
 * GA4 Ecommerce Event Tracking Functions
 * Production-ready ecommerce event implementations per GA4 standards
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

import { trackEcommerceEvent, trackEvent } from './ga4';
import type {
  EcommerceItem,
  ViewItemPayload,
  AddToCartPayload,
  RemoveFromCartPayload,
  BeginCheckoutPayload,
  AddPaymentInfoPayload,
  PurchasePayload,
} from './types';

/**
 * Prevent Purchase Event Duplication
 * Stores last purchase transaction ID and timestamp in sessionStorage
 */
const PURCHASE_TRACKING_KEY = '__dr_detoxer_last_purchase__';

const getPurchaseTrackingData = (): {
  transactionId?: string;
  timestamp?: number;
} => {
  try {
    const data = sessionStorage.getItem(PURCHASE_TRACKING_KEY);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
};

const setPurchaseTrackingData = (
  transactionId: string,
  timestamp: number
): void => {
  try {
    sessionStorage.setItem(
      PURCHASE_TRACKING_KEY,
      JSON.stringify({ transactionId, timestamp })
    );
  } catch (error) {
    console.error('[Ecommerce] Error saving purchase tracking data:', error);
  }
};

/**
 * Validate ecommerce item
 * Ensures required fields are present
 */
const validateItem = (item: any): item is EcommerceItem => {
  return (
    item &&
    typeof item.item_id === 'string' &&
    typeof item.item_name === 'string' &&
    typeof item.price === 'number' &&
    typeof item.quantity === 'number' &&
    item.item_id.length > 0 &&
    item.item_name.length > 0 &&
    item.price >= 0 &&
    item.quantity > 0
  );
};

/**
 * Convert item payload to GA4 item format
 */
const formatItem = (item: any, index?: number): EcommerceItem => {
  return {
    item_id: item.item_id || item.id || '',
    item_name: item.item_name || item.name || '',
    item_brand: item.item_brand || item.brand || 'Dr. Detoxer',
    item_category: item.item_category || item.category || 'Product',
    item_variant: item.item_variant || item.variant,
    price: item.price || 0,
    quantity: item.quantity || 1,
    currency: item.currency,
    affiliation: item.affiliation,
    discount: item.discount,
    index,
  };
};

/**
 * TRACK: view_item
 * Fired when user views a product detail page
 * 
 * GA4 Documentation:
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#view_item
 */
export const trackViewItem = (payload: ViewItemPayload): boolean => {
  // Validate required fields
  if (!payload.item_id || !payload.item_name || payload.price === undefined) {
    console.error('[Ecommerce] view_item: Missing required fields', payload);
    return false;
  }

  const item: EcommerceItem = {
    item_id: payload.item_id,
    item_name: payload.item_name,
    price: payload.price,
    quantity: payload.quantity || 1,
    currency: payload.currency || 'INR',
    item_brand: payload.item_brand || 'Dr. Detoxer',
    item_category: payload.item_category,
    item_variant: payload.item_variant,
    affiliation: payload.affiliation,
  };

  if (!validateItem(item)) {
    console.error('[Ecommerce] view_item: Invalid item format', item);
    return false;
  }

  return trackEcommerceEvent('view_item', {
    items: [item],
    currency: item.currency,
    value: item.price * item.quantity,
  });
};

/**
 * TRACK: add_to_cart
 * Fired when user adds product to cart
 * 
 * GA4 Documentation:
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#add_to_cart
 */
export const trackAddToCart = (payload: AddToCartPayload): boolean => {
  // Validate required fields
  if (!payload.item_id || !payload.item_name || payload.price === undefined) {
    console.error('[Ecommerce] add_to_cart: Missing required fields', payload);
    return false;
  }

  const item: EcommerceItem = {
    item_id: payload.item_id,
    item_name: payload.item_name,
    price: payload.price,
    quantity: payload.quantity || 1,
    currency: payload.currency || 'INR',
    item_brand: payload.item_brand || 'Dr. Detoxer',
    item_category: payload.item_category,
  };

  if (!validateItem(item)) {
    console.error('[Ecommerce] add_to_cart: Invalid item format', item);
    return false;
  }

  return trackEcommerceEvent('add_to_cart', {
    items: [item],
    currency: item.currency,
    value: (payload.cart_value || item.price * item.quantity),
  });
};

/**
 * TRACK: remove_from_cart
 * Fired when user removes product from cart
 * 
 * GA4 Documentation:
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#remove_from_cart
 */
export const trackRemoveFromCart = (
  payload: RemoveFromCartPayload
): boolean => {
  // Validate required fields
  if (!payload.item_id || !payload.item_name || payload.price === undefined) {
    console.error(
      '[Ecommerce] remove_from_cart: Missing required fields',
      payload
    );
    return false;
  }

  const item: EcommerceItem = {
    item_id: payload.item_id,
    item_name: payload.item_name,
    price: payload.price,
    quantity: payload.quantity || 1,
    currency: payload.currency || 'INR',
    item_category: payload.item_category,
  };

  if (!validateItem(item)) {
    console.error('[Ecommerce] remove_from_cart: Invalid item format', item);
    return false;
  }

  return trackEcommerceEvent('remove_from_cart', {
    items: [item],
    currency: item.currency,
    value: item.price * item.quantity,
  });
};

/**
 * TRACK: begin_checkout
 * Fired when user clicks checkout button / starts checkout flow
 * 
 * GA4 Documentation:
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#begin_checkout
 */
export const trackBeginCheckout = (payload: BeginCheckoutPayload): boolean => {
  // Validate items array
  if (!Array.isArray(payload.items) || payload.items.length === 0) {
    console.error('[Ecommerce] begin_checkout: Items array missing or empty', payload);
    return false;
  }

  // Validate and format items
  const items = payload.items
    .map((item, index) => formatItem(item, index))
    .filter(validateItem);

  if (items.length === 0) {
    console.error('[Ecommerce] begin_checkout: No valid items', payload.items);
    return false;
  }

  return trackEcommerceEvent('begin_checkout', {
    items,
    currency: payload.currency || 'INR',
    value: payload.value || 0,
    coupon: payload.coupon,
  });
};

/**
 * TRACK: add_payment_info
 * Fired when user selects payment method
 * 
 * GA4 Documentation:
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#add_payment_info
 */
export const trackAddPaymentInfo = (
  payload: AddPaymentInfoPayload
): boolean => {
  // Validate items array
  if (!Array.isArray(payload.items) || payload.items.length === 0) {
    console.error(
      '[Ecommerce] add_payment_info: Items array missing or empty',
      payload
    );
    return false;
  }

  // Validate and format items
  const items = payload.items
    .map((item, index) => formatItem(item, index))
    .filter(validateItem);

  if (items.length === 0) {
    console.error('[Ecommerce] add_payment_info: No valid items', payload.items);
    return false;
  }

  return trackEcommerceEvent('add_payment_info', {
    items,
    currency: payload.currency || 'INR',
    value: payload.value || 0,
    payment_type: payload.payment_type || 'credit_card',
    coupon: payload.coupon,
  });
};

/**
 * TRACK: purchase
 * Fired ONLY after successful payment confirmation
 * 
 * CRITICAL RULES:
 * - Must never fire twice per transaction
 * - Must include transaction_id
 * - Must include all items purchased
 * - Must persist transaction_id to prevent duplicate tracking on refresh
 * 
 * GA4 Documentation:
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#purchase
 */
export const trackPurchase = (payload: PurchasePayload): boolean => {
  // CRITICAL: Validate required purchase fields
  if (
    !payload.transaction_id ||
    payload.value === undefined ||
    !Array.isArray(payload.items) ||
    payload.items.length === 0
  ) {
    console.error('[Ecommerce] purchase: Missing required fields', payload);
    return false;
  }

  // CRITICAL: Prevent duplicate purchase events
  const trackingData = getPurchaseTrackingData();
  
  if (trackingData.transactionId === payload.transaction_id) {
    console.warn(
      `[Ecommerce] purchase: Transaction ${payload.transaction_id} already tracked. Skipping.`
    );
    return false;
  }

  // Validate and format items
  const items = payload.items
    .map((item, index) => formatItem(item, index))
    .filter(validateItem);

  if (items.length === 0) {
    console.error('[Ecommerce] purchase: No valid items', payload.items);
    return false;
  }

  // Track the purchase event
  const success = trackEcommerceEvent('purchase', {
    transaction_id: payload.transaction_id,
    affiliation: payload.affiliation || 'Dr. Detoxer Direct',
    value: payload.value,
    tax: payload.tax || 0,
    shipping: payload.shipping || 0,
    currency: payload.currency || 'INR',
    coupon: payload.coupon,
    items,
    user_id: payload.user_id,
  });

  // CRITICAL: Store transaction ID to prevent duplicate tracking on refresh
  if (success) {
    setPurchaseTrackingData(payload.transaction_id, Date.now());

    // Also track as a conversion for Google Ads
    trackEvent('purchase_conversion', {
      transaction_id: payload.transaction_id,
      value: payload.value,
      currency: payload.currency || 'INR',
      items_count: items.length,
    });
  }

  return success;
};

/**
 * Clear purchase tracking data
 * Useful for testing/debugging purposes only
 */
export const clearPurchaseTracking = (): void => {
  try {
    sessionStorage.removeItem(PURCHASE_TRACKING_KEY);
    console.log('[Ecommerce] Purchase tracking data cleared');
  } catch (error) {
    console.error('[Ecommerce] Error clearing purchase tracking:', error);
  }
};

/**
 * Check if a transaction has already been tracked
 * Useful for conditional rendering or debugging
 */
export const isTransactionTracked = (transactionId: string): boolean => {
  const trackingData = getPurchaseTrackingData();
  return trackingData.transactionId === transactionId;
};
